import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import { Button, Form, ModalDialog as Modal, Spinner } from '../ui';
import { Link, useLocation } from 'react-router-dom';
import { useRefreshBriefDetails } from '../../hooks/useBriefs';
import { Brief } from '../../services/models/brief';
import { BriefStatus } from '../../services/models/brief-status';
import { launchPopupWindow } from '../../services/popups';
import { getAddToSlackLink } from '../../services/slack';
import slackLogoSvg from '../../assets/images/slack-logo.svg';

interface Props {
  open: boolean;
  briefs: Brief[];
  onClose: () => void;
}

const ConnectReportToSlackChannelDialog: FunctionComponent<Props> = ({ open, briefs, onClose }) => {
  const location = useLocation();
  const refreshBriefDetails = useRefreshBriefDetails();

  const [selectedBrief, setSelectedBrief] = useState<Brief | null>(null);

  const close = () => {
    onClose();
  };

  const selectBrief = async (brief: Brief, event: SyntheticEvent) => {
    event.preventDefault();
    setSelectedBrief(brief);
    const addToSlackUrl = await getAddToSlackLink(brief.id, location.pathname);

    if (addToSlackUrl) {
      launchPopupWindow(
        addToSlackUrl,
        'location=yes,scrollbars=yes,status=yes',
        { height: 700, width: 800 },
        () => {
          setSelectedBrief(null);
          refreshBriefDetails(brief.id);
        }
      );
    }
  };

  const isLoading = () => selectedBrief !== null;

  const getBriefsList = () => {
    if (briefs.length === 0) {
      return (
        <div>
          You don't have any report yet. Let's{' '}
          <Link to="/dashboard/brief">create your first News Brief</Link>.
        </div>
      );
    }

    return briefs
      .filter(brief => {
        return brief.status !== BriefStatus.Draft;
      })
      .map(brief => (
        <li key={brief.id}>
          <button
            type="button"
            disabled={isLoading()}
            className="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 disabled:opacity-50 focus:outline-hidden focus:bg-gray-50"
            onClick={(e: any) => selectBrief(brief, e)}
          >
            <div className="flex flex-row items-center">
              <span className="shrink-0 grow">{brief.name}</span>
              {(() => {
                if (selectedBrief && selectedBrief.id === brief.id) {
                  return <Spinner size="sm" animation="border"></Spinner>;
                } else {
                  return <img src={slackLogoSvg} alt="" className="h-6 w-6" />;
                }
              })()}
            </div>
          </button>
        </li>
      ));
  };

  return (
    <Modal centered show={open} onHide={() => close()}>
      <Form noValidate>
        <Modal.Header closeButton>
          <Modal.Title>Select a Brief to connect with Slack</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="list-none m-0 p-0">{getBriefsList()}</ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="white" disabled={isLoading()} onClick={() => close()}>
            Close
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ConnectReportToSlackChannelDialog;
